import React from 'react';
import { GraduationCap, Briefcase, Heart, Users, Lightbulb, Target } from 'lucide-react';
import { personalInfo } from '../../mock/data';
import { formatSemester } from '../../lib/dateUtils';
import './About.css';

const About = ({ language }) => {
  const aboutContent = {
    pt: {
      title: 'Sobre Mim',
      subtitle: 'Conheça mais sobre minha jornada e paixão pela tecnologia',
      bio: personalInfo.bio.pt,
      highlights: [
        {
          icon: GraduationCap,
          title: 'Formação Acadêmica',
          description: `Estudante de Engenharia de Software na PUC Minas (${formatSemester(personalInfo.startDate, 'pt')})`,
          color: 'blue'
        },
        {
          icon: Briefcase,
          title: 'Experiência Profissional',
          description: 'Estagiário de desenvolvimento na Atalaia/Fictor Alimentos (Encerrado em Outubro/2025)',
          color: 'green'
        },
        {
          icon: Heart,
          title: 'Paixão pela Programação',
          description: 'Apaixonado por programação prática e colocar a mão na massa',
          color: 'red'
        }
      ],
      values: {
        title: 'Meus Valores',
        items: [
          {
            icon: Target,
            title: 'Proatividade',
            description: 'Sempre busco antecipar necessidades e tomar iniciativas'
          },
          {
            icon: Users,
            title: 'Trabalho em Equipe',
            description: 'Acredito no poder da colaboração e comunicação efetiva'
          },
          {
            icon: Lightbulb,
            title: 'Aprendizado Contínuo',
            description: 'Constantemente aprendendo novas tecnologias e metodologias'
          }
        ]
      },
      cta: {
        title: 'Vamos trabalhar juntos?',
        description: 'Estou sempre aberto a novas oportunidades e desafios interessantes.',
        button: 'Entre em Contato'
      }
    },
    en: {
      title: 'About Me',
      subtitle: 'Learn more about my journey and passion for technology',
      bio: personalInfo.bio.en,
      highlights: [
        {
          icon: GraduationCap,
          title: 'Academic Background',
          description: `Software Engineering student at PUC Minas (${formatSemester(personalInfo.startDate, 'en')})`,
          color: 'blue'
        },
        {
          icon: Briefcase,
          title: 'Professional Experience',
          description: 'Development intern at Atalaia/Fictor Alimentos (Ended in October/2025)',
          color: 'green'
        },
        {
          icon: Heart,
          title: 'Programming Passion',
          description: 'Passionate about practical programming and hands-on experience',
          color: 'red'
        }
      ],
      values: {
        title: 'My Values',
        items: [
          {
            icon: Target,
            title: 'Proactivity',
            description: 'Always seeking to anticipate needs and take initiative'
          },
          {
            icon: Users,
            title: 'Teamwork',
            description: 'I believe in the power of collaboration and effective communication'
          },
          {
            icon: Lightbulb,
            title: 'Continuous Learning',
            description: 'Constantly learning new technologies and methodologies'
          }
        ]
      },
      cta: {
        title: 'Let\'s work together?',
        description: 'I\'m always open to new opportunities and interesting challenges.',
        button: 'Get in Touch'
      }
    }
  };

  const content = aboutContent[language];

  return (
    <section id="about" className="about">
      <div className="container">
        <div className="about-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        <div className="about-content">
          <div className="about-main">
            <div className="about-text">
              <div className="bio-card">
                <div className="bio-content">
                  <p className="bio-text">{content.bio}</p>
                </div>
              </div>
            </div>

            <div className="highlights-grid">
              {content.highlights.map((highlight, index) => {
                const IconComponent = highlight.icon;
                return (
                  <div key={index} className={`highlight-card highlight-${highlight.color}`}>
                    <div className="highlight-icon">
                      <IconComponent size={24} />
                    </div>
                    <div className="highlight-content">
                      <h3 className="highlight-title">{highlight.title}</h3>
                      <p className="highlight-description">{highlight.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="about-sidebar">
            <div className="values-section">
              <h3 className="values-title">{content.values.title}</h3>
              <div className="values-list">
                {content.values.items.map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <div key={index} className="value-item">
                      <div className="value-icon">
                        <IconComponent size={20} />
                      </div>
                      <div className="value-content">
                        <h4 className="value-title">{value.title}</h4>
                        <p className="value-description">{value.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cta-section">
              <div className="cta-card">
                <h3 className="cta-title">{content.cta.title}</h3>
                <p className="cta-description">{content.cta.description}</p>
                <button 
                  className="btn-primary"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {content.cta.button}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;